import { Box, Tabs } from "@mantine/core";
import { IconFiles, IconMessage, IconPhoto } from "@tabler/icons-react";
import { useCallback, useMemo } from "react";
import { default as ReactMarkdown } from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import type { Reference } from "src/db";
import type { PluggableList } from "unified";
import {
    extractReferencedIdsWithNumbers,
    processMarkdownContent,
} from "../utils/markdownProcessing";
import { ImageReferenceGrid, isImageReference } from "./ImageReference";
import { InlineReference } from "./InlineReference";
import type { ComponentsWithInlineRef, InlineRefProps } from "./MessageItem";
import { MDA, MDCode, MDTable } from "./markdown/MarkdownComponents";
import { PdfFocusProvider } from "./PDFViewer/context";
import { ReferenceViewer } from "./ReferenceViewer";
import { TimeseriesPlot, useIsChartReference } from "./TimeseriesPlot";

interface AnswerWithSourcesProps {
    answer: string;
    references?: Reference[];
    children?: React.ReactNode;
    className?: string;
    dataTestId?: string;
    loading?: boolean;
    error?: boolean;
    customContent?: React.ReactNode;
    /** Rendered above the answer body (e.g. the in-progress indicator while streaming). */
    header?: React.ReactNode;
    showDisclaimer?: boolean;
}

export const AnswerWithSources = ({
    answer,
    references,
    children,
    className = "markdown",
    dataTestId = "markdown",
    loading = false,
    error = false,
    customContent,
    header,
    showDisclaimer = false,
}: AnswerWithSourcesProps) => {
    const isChartReference = useIsChartReference();
    // Charts render inline and images get their own tab — neither is listed under Sources.
    const isDocumentReference = useCallback(
        (ref: Reference) => !isChartReference(ref) && !isImageReference(ref),
        [isChartReference],
    );
    const referencedIdsWithNumbers = useMemo(
        () => extractReferencedIdsWithNumbers(answer || ""),
        [answer],
    );

    const processedReferences = useMemo(() => {
        return referencedIdsWithNumbers
            .map((refInfo) => {
                const reference = references?.find((ref) => ref.reference_id === refInfo.id);
                return reference ? { ...reference, index: refInfo.displayNumber } : null;
            })
            .filter((ref) => ref !== null && isDocumentReference(ref))
            .sort((a, b) => {
                const orderA =
                    referencedIdsWithNumbers.find((r) => r.id === a.reference_id)?.order || 0;
                const orderB =
                    referencedIdsWithNumbers.find((r) => r.id === b.reference_id)?.order || 0;
                return orderA - orderB;
            });
    }, [referencedIdsWithNumbers, references, isDocumentReference]);

    const additionalReferences = useMemo(() => {
        const mentionedIds = new Set(referencedIdsWithNumbers.map((r) => r.id));
        return references?.filter(
            (ref) => !mentionedIds.has(ref.reference_id) && isDocumentReference(ref),
        );
    }, [referencedIdsWithNumbers, references, isDocumentReference]);

    const validDocumentReferencesCount = useMemo(() => {
        return referencedIdsWithNumbers.filter((refInfo) => {
            const ref = references?.find((r) => r.reference_id === refInfo.id);
            return ref && isDocumentReference(ref);
        }).length;
    }, [referencedIdsWithNumbers, references, isDocumentReference]);

    // Cited images first (in citation order), then the rest in arrival order.
    const imageReferences = useMemo(() => {
        const images = references?.filter(isImageReference) ?? [];
        const citationOrder = new Map(referencedIdsWithNumbers.map((r) => [r.id, r.order]));
        const rank = (ref: Reference) =>
            citationOrder.get(ref.reference_id) ?? citationOrder.size + images.indexOf(ref);
        return [...images].sort((a, b) => rank(a) - rank(b));
    }, [referencedIdsWithNumbers, references]);

    const markdownComponents = useMemo(
        () =>
            ({
                table: MDTable,
                code: MDCode,
                a: MDA,
                "inline-ref": ({ node, ...props }: InlineRefProps) => {
                    const { index, display } = props;
                    const refItem = references?.find((ref) => ref.reference_id === index);

                    if (!refItem) {
                        return <span>[{display || index}]</span>;
                    }

                    if (isChartReference(refItem)) {
                        return (
                            <Box
                                component="span"
                                display="block"
                                my="sm"
                                data-testid={`timeseries-cite-${refItem.reference_id}`}
                            >
                                <InlineReference
                                    reference={refItem}
                                    index={display || index}
                                    showDisclaimer={showDisclaimer}
                                />
                                <TimeseriesPlot reference={refItem} />
                            </Box>
                        );
                    }

                    return (
                        <InlineReference
                            reference={refItem}
                            index={display || index}
                            showDisclaimer={showDisclaimer}
                        />
                    );
                },
            }) as ComponentsWithInlineRef,
        [references, showDisclaimer, isChartReference],
    );

    if (!references || references.length === 0) {
        return (
            <Box className={className} data-testid={dataTestId}>
                {header}
                <PdfFocusProvider>
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw] as PluggableList}
                        components={{
                            table: MDTable,
                            code: MDCode,
                            a: MDA,
                        }}
                    >
                        {answer}
                    </ReactMarkdown>
                </PdfFocusProvider>
            </Box>
        );
    }

    // Chart-only replies still use the answer panel; document sources get a Sources tab and
    // images an Images tab.
    const hasDocumentSources = references.some(isDocumentReference);
    const hasImages = imageReferences.length > 0;
    if (!hasDocumentSources && !hasImages) {
        return (
            <Box className={className} data-testid={dataTestId}>
                {loading
                    ? customContent
                    : !error && (
                          <PdfFocusProvider>
                              <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  rehypePlugins={[rehypeRaw] as PluggableList}
                                  components={markdownComponents}
                              >
                                  {processMarkdownContent(answer, references)}
                              </ReactMarkdown>
                          </PdfFocusProvider>
                      )}
                {customContent}
            </Box>
        );
    }

    return (
        <Tabs defaultValue="answer" variant="outline">
            <Tabs.List mb="md">
                <Tabs.Tab value="answer" leftSection={<IconMessage size={14} />}>
                    Answer
                </Tabs.Tab>
                {hasDocumentSources && (
                    <Tabs.Tab
                        data-testid="sources-tab"
                        value="sources"
                        leftSection={<IconFiles size={14} />}
                    >
                        Sources ({validDocumentReferencesCount})
                    </Tabs.Tab>
                )}
                {hasImages && (
                    <Tabs.Tab
                        data-testid="images-tab"
                        value="images"
                        leftSection={<IconPhoto size={14} />}
                    >
                        Images ({imageReferences.length})
                    </Tabs.Tab>
                )}
                {children}
            </Tabs.List>

            <Tabs.Panel value="answer" pt="md">
                <Box className={className} data-testid={dataTestId}>
                    {header}
                    {loading
                        ? customContent
                        : !error && (
                              <PdfFocusProvider>
                                  <ReactMarkdown
                                      remarkPlugins={[remarkGfm]}
                                      rehypePlugins={[rehypeRaw] as PluggableList}
                                      components={markdownComponents}
                                  >
                                      {processMarkdownContent(answer, references)}
                                  </ReactMarkdown>
                              </PdfFocusProvider>
                          )}
                    {customContent}
                </Box>
            </Tabs.Panel>

            {hasDocumentSources && (
                <Tabs.Panel value="sources" pt="md">
                    <ReferenceViewer
                        references={processedReferences}
                        additionalReferences={additionalReferences}
                        showDisclaimer={showDisclaimer}
                    />
                </Tabs.Panel>
            )}

            {hasImages && (
                <Tabs.Panel value="images" pt="md">
                    <ImageReferenceGrid references={imageReferences} />
                </Tabs.Panel>
            )}
        </Tabs>
    );
};
